/**
 * PixiSymbolCell — Individual symbol display object for the PixiJS cascade slot grid.
 * Renders either a sprite texture or a colored rectangle with text label.
 */
import { Container, Graphics, Sprite, Text, TextStyle, Texture } from 'pixi.js';
import type { Spine } from '@esotericsoftware/spine-pixi-v8';
import type { CascadeSymbolConfig, GridCell } from '@core/types/cascade-slot.types';

export class PixiSymbolCell {
  readonly container = new Container();

  private bg = new Graphics();
  private sprite: Sprite | null = null;
  private spineNode: Spine | null = null;
  private label: Text;
  private border = new Graphics();
  private winRing = new Graphics();

  private cellWidth: number;
  private cellHeight: number;

  constructor(width: number, height: number) {
    this.cellWidth = width;
    this.cellHeight = height;

    // Background rect (shown when no sprite texture)
    this.container.addChild(this.bg);

    // Border (always present, style changes based on state)
    this.container.addChild(this.border);

    // Win ring (shown when cell is winning)
    this.winRing.visible = false;
    this.container.addChild(this.winRing);

    // Label text (symbol id or multiplier)
    this.label = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: 'Inter, Arial, sans-serif',
        fontSize: 14,
        fontWeight: 'bold',
        fill: '#ffffff',
        align: 'center',
      }),
    });
    this.label.anchor.set(0.5);
    this.label.position.set(width / 2, height / 2);
    this.container.addChild(this.label);

    this.drawBorder(false);
  }

  // ── Spine Instance Management ──────────────────────────────────

  /** Attach a pre-loaded Spine instance to this cell. */
  attachSpine(spine: Spine): void {
    if (this.spineNode === spine) return;

    // Remove existing spine if any (don't destroy — pool owns it)
    if (this.spineNode && this.spineNode.parent) {
      this.container.removeChild(this.spineNode);
    }

    this.spineNode = spine;

    // Scale spine to fit cell dimensions
    const bounds = spine.getBounds();
    if (bounds.width > 0 && bounds.height > 0) {
      const scaleX = this.cellWidth / bounds.width;
      const scaleY = this.cellHeight / bounds.height;
      const scale = Math.min(scaleX, scaleY);
      spine.scale.set(scale);
    }

    // Center in cell
    spine.position.set(this.cellWidth / 2, this.cellHeight / 2);

    // Insert above bg but below border/winRing/label (index 1)
    const insertIndex = this.sprite ? 2 : 1;
    this.container.addChildAt(spine, Math.min(insertIndex, this.container.children.length));
  }

  /** Detach and return the Spine instance (for pool return). Does NOT destroy it. */
  detachSpine(): Spine | null {
    const spine = this.spineNode;
    if (!spine) return null;

    if (spine.parent === this.container) {
      this.container.removeChild(spine);
    }
    this.spineNode = null;
    return spine;
  }

  // ── Update ─────────────────────────────────────────────────────

  /** Update the cell with new data */
  update(cell: GridCell, symbol: CascadeSymbolConfig, texture: Texture | null): void {
    const useSprite = symbol.displayMode === 'sprite' && texture != null;
    const useSpine = symbol.displayMode === 'spine' && this.spineNode != null;

    // --- Background ---
    this.bg.clear();
    if (!useSprite && !useSpine) {
      this.bg.rect(0, 0, this.cellWidth, this.cellHeight)
        .fill({ color: symbol.color });
    }
    this.bg.visible = !useSprite && !useSpine;

    // --- Sprite ---
    if (useSprite && texture) {
      if (!this.sprite) {
        this.sprite = new Sprite(texture);
        // Insert sprite above bg but below border/winRing/label
        this.container.addChildAt(this.sprite, 1);
      } else {
        this.sprite.texture = texture;
      }
      this.sprite.width = this.cellWidth;
      this.sprite.height = this.cellHeight;
      this.sprite.visible = true;
    } else if (this.sprite) {
      this.sprite.visible = false;
    }

    // --- Spine ---
    if (this.spineNode) {
      this.spineNode.visible = useSpine;
    }

    // --- Label ---
    const showMultiplier = cell.multiplierValue > 0;
    if (useSprite || useSpine) {
      // When using sprite or spine, only show multiplier overlay, not symbol id
      this.label.visible = showMultiplier;
      if (showMultiplier) {
        this.label.text = `x${cell.multiplierValue}`;
        (this.label.style as TextStyle).fontSize = Math.max(10, Math.min(18, this.cellHeight * 0.35));
        (this.label.style as TextStyle).fill = symbol.textColor;
        (this.label.style as TextStyle).fontWeight = 'bold';
      }
    } else {
      this.label.visible = true;
      if (showMultiplier) {
        this.label.text = `x${cell.multiplierValue}`;
        (this.label.style as TextStyle).fontSize = Math.max(10, Math.min(18, this.cellHeight * 0.35));
        (this.label.style as TextStyle).fontWeight = 'bold';
      } else {
        this.label.text = symbol.id;
        (this.label.style as TextStyle).fontSize = Math.max(10, Math.min(16, this.cellHeight * 0.3));
        (this.label.style as TextStyle).fontWeight = '600';
      }
      (this.label.style as TextStyle).fill = symbol.textColor;
    }
    this.label.position.set(this.cellWidth / 2, this.cellHeight / 2);

    // --- Border & Win Ring ---
    this.drawBorder(cell.isWinning);
    this.drawWinRing(cell.isWinning);
  }

  /** Reset all visual state (alpha, scale, position offsets) */
  resetAnimations(): void {
    this.container.alpha = 1;
    this.container.scale.set(1);
    // y is managed by grid layout, so we don't reset it here

    // Reset spine transform (but don't stop animation — that's SpineSymbolManager's job)
    if (this.spineNode) {
      this.spineNode.alpha = 1;
      this.spineNode.position.set(this.cellWidth / 2, this.cellHeight / 2);
    }
  }

  resize(width: number, height: number): void {
    this.cellWidth = width;
    this.cellHeight = height;
    this.label.position.set(width / 2, height / 2);
  }

  destroy(): void {
    // Detach spine (don't destroy — pool owns it)
    this.detachSpine();

    if (this.sprite) {
      this.sprite.destroy();
      this.sprite = null;
    }
    this.bg.destroy();
    this.label.destroy();
    this.border.destroy();
    this.winRing.destroy();
    this.container.destroy({ children: true });
  }

  private drawBorder(isWinning: boolean): void {
    this.border.clear();
    if (isWinning) {
      // 2px amber-400 border
      this.border.rect(0, 0, this.cellWidth, this.cellHeight)
        .stroke({ color: 0xfbbf24, width: 2 });
    } else {
      // 1px zinc-700/50 border
      this.border.rect(0, 0, this.cellWidth, this.cellHeight)
        .stroke({ color: 0x3f3f46, width: 1, alpha: 0.5 });
    }
  }

  private drawWinRing(isWinning: boolean): void {
    this.winRing.clear();
    if (!isWinning) {
      this.winRing.visible = false;
      return;
    }
    this.winRing.visible = true;
    // Golden ring around the cell (inset by 1px to stay within bounds)
    const inset = 1;
    this.winRing.roundRect(inset, inset, this.cellWidth - inset * 2, this.cellHeight - inset * 2, 2)
      .stroke({ color: 0xfbbf24, width: 2, alpha: 0.8 });
  }
}
